import React, { useState, useEffect, useCallback } from 'react';
import { GUITAR_STRINGS, CHROMATIC_NOTES, getNoteAtFret } from '../../utils/guitarTheory';
import { soundEngine } from '../../utils/audio';
import { Trophy, Flame, HelpCircle, RotateCcw, Sparkles, Volume2, CheckCircle2 } from 'lucide-react';
import confetti from 'canvas-confetti';

interface TargetPrompt {
  stringIndex: number; // 0 (High E) to 5 (Low E)
  fret: number;
  note: string;
}

export const FretboardGame: React.FC = () => {
  const [gameMode, setGameMode] = useState<'quest' | 'explorer'>('quest');
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [target, setTarget] = useState<TargetPrompt | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeFret, setActiveFret] = useState<{ stringIdx: number; fret: number } | null>(null);

  // Generate a new random target prompt
  const generateNewTarget = useCallback(() => {
    // String 0 (High E) to 5 (Low E)
    const randomStringIdx = Math.floor(Math.random() * 6);
    // Frets 0 to 12
    const randomFret = Math.floor(Math.random() * 13);
    const { note } = getNoteAtFret(randomStringIdx, randomFret);

    setTarget({
      stringIndex: randomStringIdx,
      fret: randomFret,
      note,
    });
    setFeedback(null);
  }, []);

  useEffect(() => {
    if (gameMode === 'quest' && !target) {
      generateNewTarget();
    }
  }, [gameMode, target, generateNewTarget]);

  // Click handler for a fret
  const handleFretClick = (stringIdx: number, fret: number) => {
    const { note, freq } = getNoteAtFret(stringIdx, fret);
    soundEngine.playGuitarPluck(freq, 2.2, 0.5);
    setActiveFret({ stringIdx, fret });
    setTimeout(() => setActiveFret(null), 600);

    if (gameMode === 'explorer') {
      return;
    }

    if (!target) return;

    // Check if user clicked the right note on the specified string
    const isCorrect = stringIdx === target.stringIndex && fret === target.fret;

    if (isCorrect) {
      soundEngine.playSuccessSfx();
      const newStreak = streak + 1;
      setStreak(newStreak);
      setScore(score + 10 + Math.min(newStreak * 2, 20));
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
      }

      setFeedback({
        type: 'success',
        message: `Yay! Note ${note} found on ${GUITAR_STRINGS[stringIdx].name}! 🌸`,
      });

      confetti({
        particleCount: 35,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#F472B6', '#C084FC', '#38BDF8', '#FDE047'],
      });

      setTimeout(() => {
        generateNewTarget();
      }, 1400);
    } else {
      soundEngine.playIncorrectSfx();
      setStreak(0);
      setFeedback({
        type: 'error',
        message: `Oops! You played ${note} on ${GUITAR_STRINGS[stringIdx].name}. We're looking for ${target.note} on ${GUITAR_STRINGS[target.stringIndex].name}!`,
      });
    }
  };

  const handleResetQuest = () => {
    soundEngine.playPopSfx();
    setScore(0);
    setStreak(0);
    generateNewTarget();
  };

  // Position markers on guitar fretboard: 3, 5, 7, 9, 12 (double)
  const isMarkerFret = (fret: number) => [3, 5, 7, 9, 12].includes(fret);

  return (
    <div className="space-y-6">
      
      {/* Game Mode Selector & Stats Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-6 rounded-3xl border-2 border-pink-200 shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              soundEngine.playPopSfx();
              setGameMode('quest');
            }}
            className={`px-4 py-2 rounded-2xl font-bold text-xs sm:text-sm transition-all cursor-pointer ${
              gameMode === 'quest'
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-xs'
                : 'bg-pink-50 text-purple-700 hover:bg-pink-100'
            }`}
          >
            🎯 Note Hunter Quest
          </button>
          
          <button
            onClick={() => {
              soundEngine.playPopSfx();
              setGameMode('explorer');
            }}
            className={`px-4 py-2 rounded-2xl font-bold text-xs sm:text-sm transition-all cursor-pointer ${
              gameMode === 'explorer'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-xs'
                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
            }`}
          >
            🔍 Free Fretboard Explorer
          </button>
        </div>

        {/* Quest Mode Stats or Explorer Controls */}
        {gameMode === 'quest' ? (
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span>Score: {score}</span>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 text-xs font-bold">
              <Flame className="w-4 h-4 fill-current" />
              <span>Streak: {streak}</span>
            </div>

            <button
              onClick={handleResetQuest}
              className="p-1.5 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer"
              title="Reset Quest"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-gray-600 flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAllNotes}
                onChange={(e) => {
                  soundEngine.playPopSfx();
                  setShowAllNotes(e.target.checked);
                }}
                className="w-4 h-4 accent-pink-500 rounded"
              />
              <span>Show All Fretboard Notes</span>
            </label>
          </div>
        )}
      </div>

      {/* Quest Target Challenge Banner */}
      {gameMode === 'quest' && target && (
        <div className="p-5 rounded-3xl bg-gradient-to-r from-pink-100 via-purple-100 to-rose-100 border-2 border-pink-300 text-center space-y-2 shadow-xs">
          <div className="text-xs font-bold uppercase tracking-wide text-pink-700 flex items-center justify-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Melodalis Note Challenge</span>
          </div>

          <div className="text-2xl sm:text-3xl font-extrabold text-purple-900">
            Find Note <span className="text-pink-600 underline decoration-wavy decoration-pink-300">{target.note}</span> on the <span className="text-indigo-600">{GUITAR_STRINGS[target.stringIndex].name}</span> string!
          </div>

          <p className="text-xs text-purple-800/80">
            Click on the correct fret along the guitar neck below. Fret 0 is the open string!
          </p>

          {feedback && (
            <div className={`mt-2 py-2 px-4 rounded-xl text-xs font-bold inline-block animate-bounce ${
              feedback.type === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-rose-100 text-rose-700 border border-rose-300'
            }`}>
              {feedback.message}
            </div>
          )}
        </div>
      )}

      {/* Interactive Fretboard Visualizer (Horizontal Scrollable on Mobile) */}
      <div className="bg-amber-950 p-4 sm:p-6 rounded-3xl shadow-xl border-4 border-amber-900 overflow-x-auto">
        
        {/* Fretboard Inner Neck */}
        <div className="min-w-[780px] select-none">
          
          {/* Top Fret Number Headers (0 to 12) */}
          <div className="grid grid-cols-[80px_repeat(13,1fr)] text-center text-amber-200 text-xs font-bold pb-2 border-b border-amber-800">
            <div>String</div>
            {Array.from({ length: 13 }).map((_, f) => (
              <div key={f} className="flex flex-col items-center">
                <span>{f === 0 ? 'Open (0)' : f}</span>
                {isMarkerFret(f) && (
                  <span className="text-[10px] text-amber-400">
                    {f === 12 ? '●●' : '●'}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* 6 Guitar Strings (from 0: High E to 5: Low E) */}
          <div className="divide-y divide-amber-900/60 my-2">
            {[0, 1, 2, 3, 4, 5].map((strIdx) => {
              const stringInfo = GUITAR_STRINGS[strIdx];
              return (
                <div key={strIdx} className="grid grid-cols-[80px_repeat(13,1fr)] items-center h-12 relative group">
                  
                  {/* Left Label: String Name */}
                  <div className="pr-2 font-bold text-xs text-amber-100 flex items-center justify-between">
                    <span>{stringInfo.name.split(' ')[0]}</span>
                    <span className="text-[10px] text-amber-400">{stringInfo.baseNote}</span>
                  </div>

                  {/* Horizontal Metallic Guitar String Line running through all frets */}
                  <div
                    className="absolute left-[80px] right-0 pointer-events-none z-10 bg-gradient-to-b from-gray-200 via-gray-400 to-gray-300 shadow-sm"
                    style={{
                      height: `${Math.max(1.5, 4.2 - strIdx * 0.5)}px`,
                      top: 'calc(50% - 1px)',
                    }}
                  />

                  {/* 13 Fret Slots (0 to 12) */}
                  {Array.from({ length: 13 }).map((_, fret) => {
                    const { note, isNatural } = getNoteAtFret(strIdx, fret);
                    const isTargetFret = target && target.stringIndex === strIdx && target.fret === fret;
                    const isActive = activeFret && activeFret.stringIdx === strIdx && activeFret.fret === fret;

                    return (
                      <button
                        key={fret}
                        onClick={() => handleFretClick(strIdx, fret)}
                        className={`h-full relative border-r-2 ${
                          fret === 0 ? 'border-amber-400 border-r-4' : 'border-amber-600/50'
                        } flex items-center justify-center hover:bg-amber-800/40 transition-colors cursor-pointer z-20`}
                      >
                        {/* Note Badge on Fret */}
                        {(showAllNotes || (gameMode === 'quest' && isTargetFret && feedback?.type === 'success') || isActive) ? (
                          <div
                            className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shadow-md transition-all ${
                              isActive
                                ? 'bg-yellow-400 text-yellow-950 scale-125 ring-2 ring-white animate-bounce'
                                : isNatural
                                ? 'bg-white text-gray-800 border border-gray-300'
                                : 'bg-pink-400 text-white'
                            }`}
                          >
                            {note}
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full opacity-0 hover:opacity-100 bg-white/20 hover:scale-110 transition-all flex items-center justify-center text-[10px] text-white">
                            {note}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Bottom Position Inlay Markers row */}
          <div className="grid grid-cols-[80px_repeat(13,1fr)] text-center text-amber-400 text-xs pt-1">
            <div />
            {Array.from({ length: 13 }).map((_, f) => (
              <div key={f} className="flex justify-center items-center">
                {f === 12 ? (
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-200" />
                    <span className="w-2 h-2 rounded-full bg-amber-200" />
                  </div>
                ) : isMarkerFret(f) ? (
                  <span className="w-2 h-2 rounded-full bg-amber-200" />
                ) : null}
              </div>
            ))}
          </div>

        </div>

      </div>

      {/* Helper Legend / Info */}
      <div className="flex flex-wrap items-center justify-between text-xs text-gray-500 bg-white p-4 rounded-2xl border border-pink-100">
        <div className="flex items-center gap-3">
          <span className="font-bold text-purple-900">Fretboard Inlays:</span>
          <span>Frets 3, 5, 7, 9 have single dots (●).</span>
          <span>Fret 12 has the double dot (●●) octave mark!</span>
        </div>
        <div className="flex items-center gap-1.5 text-pink-600 font-semibold mt-2 sm:mt-0">
          <Volume2 className="w-4 h-4" />
          <span>Real acoustic string audio generated on click</span>
        </div>
      </div>

    </div>
  );
};

